import { type Message as AgUiMessage, type UserMessage as AgUiUserMessage, CopilotKitCoreRuntimeConnectionStatus, useAgent, useCopilotKit, UseAgentUpdate } from '@copilotkit/react-core/v2';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useAuth } from 'shared/context/AuthContext';
import { useCurrentClient } from 'shared/context/ClientContext';
import { useOptimisticallyBumpChatUpdatedAt } from 'shared/copilot/api';
import { type CopilotAgentId, type CopilotSurface } from 'shared/copilot/copilotSurfaces';
import { buildCopilotAgentConversationReturn, type CopilotAgentConversationReturn } from 'shared/copilot/hooks/buildCopilotAgentConversationReturn';
import { useCopilotAgentChatMetaSync } from 'shared/copilot/hooks/useCopilotAgentChatMetaSync';
import { useCopilotChatHydration } from 'shared/copilot/hooks/useCopilotChatHydration';
import { useCopilotCsvAttachment } from 'shared/copilot/hooks/useCopilotCsvAttachment';
import { useCopilotDatasourceSession, type DatasourceUserSelection } from 'shared/copilot/hooks/useCopilotDatasourceSession';
import { revokePreviewUrl, useCopilotImageAttachments } from 'shared/copilot/hooks/useCopilotImageAttachments';
import { type KnowledgeCorpusEntityState } from 'shared/api/copilot';
import { useCopilotKnowledgeSourcesSession } from 'shared/copilot/hooks/useCopilotKnowledgeSourcesSession';
import { resetCopilotFrontendToolElementEffectState, resetCopilotFrontendToolHighlights } from 'shared/copilot/hooks/useCopilotFrontendToolElementEffects';
import { buildAgentCsvRoutingPatch, getUserMessageCsvAttachMode, resolveCsvRoutingMode } from 'shared/copilot/utils/csvRoutingMode';
import { buildAgentSkillPinsPatch } from 'src/copilot/utils/skillTags';
import { useCopilotSourceSelectionPersistence } from 'shared/copilot/hooks/useCopilotSourceSelectionPersistence';
import { buildPersistedSourceSelection, writeLastSourceSelection } from 'shared/copilot/utils/copilotSourceSelectionStorage';
import logger from 'shared/logger';
const log = logger().child({
  namespace: 'copilot/agentConversation'
});
export interface CopilotSendToolResultArgs {
  toolCallId: string;
  toolName: string;
  result: Record<string, unknown>;
}
interface AlvieCopilotState {
  _alvie_chat_id?: string;
  _alvie_chat_title?: string;
  chat_id?: string;
}
export interface SendUserMessageOptions {
  /**
   * Whether to attach the composer's currently uploaded images to this
   * message and clear them afterward. Defaults to `true`, for the composer's
   * own submit. A canned/system message — a suggested-prompt button, plan
   * approval, or data-source confirmation — passes `false`: none of those are
   * about whatever the user happens to have staged for their own next
   * question, so it must stay in the composer untouched.
   */
  includeComposerAttachments?: boolean;
  /**
   * Slash-chip ids from MessageComposer; mapped to Flow integer skill ids when pinning.
   * Pass `[]` to clear the previous pin. Omit the field to leave the current pin in place.
   */
  skillIds?: Array<string | number>;
}

/**
 * One just-sent image's local blob preview and real filename, keyed by the
 * `gcsUri` the message content carries — the composer already has both the
 * instant the upload succeeds, well before the thread's own signed-URL
 * lookup would ever produce them for a brand-new URI. See
 * `justSentImagePreviewByGcsUri` on {@link CopilotAgentConversationReturn}.
 */
export interface JustSentImagePreview {
  previewUrl: string;
  filename: string;
}
export interface CopilotConversationApi {
  messages: AgUiMessage[];
  /**
   * Sends a user turn. Returns false when the agent is still running and the
   * message was not sent, so callers that lock a local success state can stay
   * retryable instead of looking finished.
   */
  sendUserMessage: (message: string, options?: SendUserMessageOptions) => boolean;
  sendToolResult: (args: CopilotSendToolResultArgs) => void;
  isLoading: boolean;
  /** True while a `loadChat` hydration fetch is in flight (messages may be temporarily empty). */
  isHydratingChat: boolean;
  /**
   * True when the active chat's history failed to load for a transient reason (5xx, network). The chat is
   * kept, so surfaces show an error instead of an empty new-chat view.
   */
  chatLoadFailed: boolean;
  /** Re-fetches the history of the chat whose load failed; no-op when nothing failed. */
  retryChatLoad: () => void;
  stopGeneration: () => void;
  currentChatId: string | null;
  createNewChat: (options?: {
    initialMessage?: string;
  }) => void;
  loadChat: (chatId: string) => void;
}
interface UseCopilotAgentConversationArgs {
  agentId: CopilotAgentId;
  activeThreadId: string;
  currentChatId: string | null;
  onChatIdChange?: (chatId: string) => void;
  onNewChat?: () => string;
  onLoadChat?: (chatId: string) => void;
  onTitleGenerated?: (chatId: string) => void;
  /**
   * Called after a chat that failed to load was replaced with the fallback chat or a new one (missing/foreign/deleted).
   * `fallbackChatId` is the chat reopened instead; `null` means a new chat was started.
   */
  onChatUnavailable?: (chatId: string, fallbackChatId: string | null) => void;
  /** Chat to load instead of one that failed to load; `null` (or no callback) starts a new chat. */
  resolveFallbackChatId?: (failedChatId: string) => string | null;
  /** Called after a loaded chat's history was fetched successfully (the chat is confirmed to exist). */
  onChatHydrated?: (chatId: string) => void;
  /** When true, syncs datasource picker selection into agent.state on send/load/new. */
  enableDatasourceSession: boolean;
  /** When true, syncs knowledge-source picker selection into agent.state. Defaults to `enableDatasourceSession`. */
  enableKnowledgeSourceSession?: boolean;
  /** When true, wires the standalone CSV attachment picker into agent.state and message content. */
  enableCsvAttachment: boolean;
  /** When true, lets the composer attach images as message content parts. */
  enableImageAttachment: boolean;
  /** Scopes persisted source-picker state so drawer and fullscreen do not clobber each other. */
  sourceSelectionSurface: CopilotSurface;
}

/**
 * Bridges one CopilotKit agent instance into Alvie conversation APIs
 * (messages, send, load, hydrate, optional datasource sync).
 */
export const useCopilotAgentConversation = ({
  agentId,
  activeThreadId,
  currentChatId,
  onChatIdChange,
  onNewChat,
  onLoadChat,
  onTitleGenerated,
  onChatUnavailable,
  resolveFallbackChatId,
  onChatHydrated,
  enableDatasourceSession,
  enableKnowledgeSourceSession = enableDatasourceSession,
  enableCsvAttachment,
  enableImageAttachment,
  sourceSelectionSurface
}: UseCopilotAgentConversationArgs): CopilotAgentConversationReturn => {
  const {
    agent
  } = useAgent({
    agentId,
    updates: [UseAgentUpdate.OnMessagesChanged, UseAgentUpdate.OnStateChanged, UseAgentUpdate.OnRunStatusChanged]
  });
  const {
    copilotkit
  } = useCopilotKit();
  const {
    currentClient
  } = useCurrentClient();
  const {
    currentUser
  } = useAuth();
  const clientId = currentClient.id;
  const userId = currentUser?.id;
  const externalWorkspaceId = clientId;
  const bumpChatUpdatedAt = useOptimisticallyBumpChatUpdatedAt(clientId, userId);
  const datasourcePartsRef = useRef<DatasourceUserSelection>({
    optionIds: [],
    entities: [],
    cirrusSource: null
  });
  const knowledgePartsRef = useRef<KnowledgeCorpusEntityState[]>([]);
  const publishLastUsedSourceSelection = (): void => {
    if (!enableDatasourceSession && !enableKnowledgeSourceSession) return;
    writeLastSourceSelection(clientId, sourceSelectionSurface, buildPersistedSourceSelection({
      optionIds: enableDatasourceSession ? datasourcePartsRef.current.optionIds : [],
      entities: enableDatasourceSession ? datasourcePartsRef.current.entities : [],
      cirrusSource: enableDatasourceSession ? datasourcePartsRef.current.cirrusSource : null,
      knowledgeSources: enableKnowledgeSourceSession ? knowledgePartsRef.current : []
    }));
  };
  const datasource = useCopilotDatasourceSession({
    agent,
    externalWorkspaceId,
    onUserSelectionChange: selection => {
      datasourcePartsRef.current = selection;
      publishLastUsedSourceSelection();
    }
  });
  const knowledgeSources = useCopilotKnowledgeSourcesSession({
    agent,
    onUserSelectionChange: sources => {
      knowledgePartsRef.current = sources;
      publishLastUsedSourceSelection();
    }
  });
  datasourcePartsRef.current = {
    optionIds: datasource.selectedDatasourceIds,
    entities: datasource.selectedEntities,
    cirrusSource: datasource.selectedCirrusSource
  };
  knowledgePartsRef.current = knowledgeSources.selectedKnowledgeSources;
  const csvAttachment = useCopilotCsvAttachment({
    agent,
    clientId,
    userId
  });
  const imageAttachments = useCopilotImageAttachments({
    clientId
  });
  const {
    clearAttachments: clearImageAttachments,
    takeAttachments: takeImageAttachments
  } = imageAttachments;
  // Keyed by gcsUri, for the exact images just sent — see JustSentImagePreview.
  // Never pruned: entries are a handful of short strings each, so even a very
  // long session's worth of them is negligible; the blobs they point to (the
  // actual memory cost) are revoked by the thread once the real signed URL
  // supersedes each one (see CopilotUserMessageAttachments), on thread switch
  // below, or — for whatever is still unsuperseded — on unmount.
  const [justSentImagePreviewByGcsUri, setJustSentImagePreviewByGcsUri] = useState<Record<string, JustSentImagePreview>>({});
  // Mirrors the state above so the unmount cleanup can revoke whatever is
  // current without closing over a stale snapshot from whenever the effect
  // itself first ran.
  const justSentImagePreviewRef = useRef<Record<string, JustSentImagePreview>>({});
  const updateJustSentImagePreviews = useCallback((updater: (existing: Record<string, JustSentImagePreview>) => Record<string, JustSentImagePreview>) => {
    setJustSentImagePreviewByGcsUri(existing => {
      const next = updater(existing);
      justSentImagePreviewRef.current = next;
      return next;
    });
  }, []);
  useEffect(() => () => {
    // Leaving fullscreen Copilot unmounts the message list (and with it
    // every per-message optimistic preview) well before this hook itself
    // ever unmounts, so most entries are gone by the time this runs. But
    // this hook can unmount too — a full app teardown, logout, or simply
    // switching agents — and whatever is still unsuperseded at that point
    // has no other owner left to revoke it.
    Object.values(justSentImagePreviewRef.current).forEach(entry => {
      revokePreviewUrl(entry.previewUrl);
    });
  }, []);
  const {
    restoreSessionAttachment,
    cancelAttachment: cancelCsvAttachment,
    syncCsvRoutingState,
    clearPerTurnUploadedCsvFiles
  } = csvAttachment;

  // A run requested while CopilotKit still serves the provisional agent (before the runtime
  // /info call resolves). It starts on the real agent once that is bound — see the swap effect.
  const deferredRunRef = useRef<{
    resolve: (result: unknown) => void;
    reject: (error: unknown) => void;
  } | null>(null);
  const [hasDeferredRun, setHasDeferredRun] = useState(false);
  const messages = agent.messages;
  // A deferred run counts as running: the composer stays locked and the next send is refused.
  const isLoading = agent.isRunning || hasDeferredRun;
  const agentState = agent.state as AlvieCopilotState | undefined;
  const titleFiredForChatRef = useRef<string | null>(null);
  const lastEmittedChatIdRef = useRef<string | null>(null);
  const activeThreadIdRef = useRef<string | null>(null);
  const hasSelectedDatasourceRef = useRef(false);

  // Chat whose history failed to load for a transient reason; cleared whenever the thread changes.
  const [failedChatLoadId, setFailedChatLoadId] = useState<string | null>(null);
  const createNewChatRef = useRef<() => void>(() => {});
  const loadChatRef = useRef<(chatId: string) => void>(() => {});
  const {
    setHydrationTargetChatId,
    hydrationTargetChatId,
    isHydrating
  } = useCopilotChatHydration({
    agent,
    agentId,
    clientId,
    userId,
    initialChatId: currentChatId ?? null,
    enableCsvAttachment,
    hasSelectedDatasource: datasource.hasSelectedDatasource,
    activeThreadIdRef,
    hasSelectedDatasourceRef,
    restoreSessionAttachment,
    onHydrated: onChatHydrated,
    onHydrationFailed: (failedChatId, reason) => {
      if (activeThreadIdRef.current !== failedChatId) return;
      // A 5xx/network failure says nothing about whether the chat exists: keep it (and its URL) so a
      // retry or refresh continues it instead of dropping the user into another chat. Flag the failure so
      // the surface shows an error with a retry rather than an empty new-chat view for this existing chat.
      if (reason === 'transient') {
        setFailedChatLoadId(failedChatId);
        return;
      }
      // Leave the bad thread so the next send cannot reuse a missing/foreign chat id: go back to the
      // fallback chat when there is one, else start a new chat. Route sync then replaces
      // `/ai/chat/<id>` → `/ai/chat` when currentChatId clears.
      const fallbackChatId = resolveFallbackChatId?.(failedChatId) ?? null;
      if (fallbackChatId == null) {
        createNewChatRef.current();
      } else {
        loadChatRef.current(fallbackChatId);
      }
      onChatUnavailable?.(failedChatId, fallbackChatId);
    }
  });
  const isHydratingChat = hydrationTargetChatId != null || isHydrating;
  const chatLoadFailed = failedChatLoadId != null && !isHydratingChat;
  const resetAgentState = useCallback(() => {
    agent.setState({});
  }, [agent]);
  const switchAgentThread = useCallback((threadId: string) => {
    if (agent.threadId !== threadId) {
      agent.threadId = threadId;
    }
    agent.setMessages([]);
    resetAgentState();
    if (enableCsvAttachment) {
      cancelCsvAttachment();
    }
    if (enableImageAttachment) {
      clearImageAttachments();
      // The messages that referenced these previews are gone with
      // agent.setMessages([]) above, and no future message in the new
      // thread will ever carry the old thread's gcsUris — so nothing will
      // read these blobs again and they can be revoked now, rather than
      // left for the thread's own per-message cleanup (which never runs
      // once its message is gone) or the page unload.
      updateJustSentImagePreviews(existing_0 => {
        Object.values(existing_0).forEach(entry_0 => {
          revokePreviewUrl(entry_0.previewUrl);
        });
        return {};
      });
    }
    titleFiredForChatRef.current = null;
    lastEmittedChatIdRef.current = null;
    activeThreadIdRef.current = threadId;
    setFailedChatLoadId(null);
  }, [agent, cancelCsvAttachment, clearImageAttachments, enableCsvAttachment, enableImageAttachment, resetAgentState, updateJustSentImagePreviews]);
  const boundAgentRef = useRef(agent);
  const boundAgentIdRef = useRef(agentId);
  const cancelDeferredRun = useCallback(() => {
    const deferredRun = deferredRunRef.current;
    if (deferredRun == null) return;
    deferredRunRef.current = null;
    setHasDeferredRun(false);
    deferredRun.resolve(undefined);
  }, []);

  // stopAgent only asks the runtime to stop the thread, and the runtime tracks
  // runs per process: with several Cloud Run instances the request usually lands
  // on one that isn't streaming this run and nothing stops. Detaching closes our
  // own stream so the UI stops waiting regardless; the dropped connection then
  // cancels the upstream Flow request (see RequestScopedHttpAgent in
  // app/api/copilot/requestScopedHttpAgent.ts).
  const stopAgentRun = useCallback((target: typeof agent) => {
    copilotkit.stopAgent({
      agent: target
    });
    target.detachActiveRun().catch(error => {
      log.warn({
        error,
        agentId
      }, 'failed to detach copilot agent run');
    });
  }, [agentId, copilotkit]);

  // A run may only start on an agent the UI will stay bound to. CopilotKit serves a provisional
  // agent until the runtime /info call resolves, then swaps in the real one; a run started on the
  // provisional agent would keep streaming into it after the swap. Once the runtime connection has
  // failed there is no swap coming (runtimeUrl is fixed, so CopilotKit never reconnects), and the
  // provisional agent is the one to run on.
  const isAgentRunnable = useCallback(() => copilotkit.getAgent(agentId) === agent || copilotkit.runtimeConnectionStatus === CopilotKitCoreRuntimeConnectionStatus.Error, [agent, agentId, copilotkit]);

  // Runs requested before the agent is runnable wait here; the bind effect below starts them.
  const runAgentWhenReady = useCallback(async (): Promise<unknown> => {
    if (isAgentRunnable()) {
      return await copilotkit.runAgent({
        agent
      });
    }
    log.info({
      agentId
    }, 'copilot runtime not connected yet; deferring run until the agent is ready');
    cancelDeferredRun();
    setHasDeferredRun(true);
    return await new Promise((resolve, reject) => {
      deferredRunRef.current = {
        resolve,
        reject
      };
    });
  }, [agent, agentId, cancelDeferredRun, copilotkit, isAgentRunnable]);
  const flushDeferredRun = useCallback(() => {
    const deferredRun_0 = deferredRunRef.current;
    if (deferredRun_0 == null) return;
    deferredRunRef.current = null;
    setHasDeferredRun(false);
    copilotkit.runAgent({
      agent
    }).then(deferredRun_0.resolve, deferredRun_0.reject);
  }, [agent, copilotkit]);

  // Re-runs the bind effect when the connection settles: a run deferred while connecting must
  // start once the connection fails too, since CopilotKit then keeps the same provisional agent.
  const runtimeConnectionStatus = copilotkit.runtimeConnectionStatus;

  // Layout effect: the carry-over below runs before paint, so the freshly swapped-in (still
  // empty) agent is never shown for a frame and the drawer does not flash the new-chat state.
  useLayoutEffect(() => {
    const previousAgent = boundAgentRef.current;
    boundAgentRef.current = agent;
    const isAgentSwitch = boundAgentIdRef.current !== agentId;
    boundAgentIdRef.current = agentId;

    // Nothing renders, reports or stops the replaced agent any more, so a run still streaming on it
    // would leave the UI idle and stop detaching the wrong instance. End it before rebinding.
    if (previousAgent !== agent && previousAgent.isRunning) {
      log.warn({
        agentId
      }, 'copilot agent instance replaced during a run; stopping the run on the replaced agent');
      stopAgentRun(previousAgent);
    }

    // A switch to another agent (dev agent switcher) has never seen the active thread; only a
    // provisional-to-runtime swap of the same agent carries the thread over.
    if (isAgentSwitch || activeThreadIdRef.current !== activeThreadId) {
      // The deferred run belonged to the thread being left, whose messages are cleared below.
      cancelDeferredRun();
      switchAgentThread(activeThreadId);
      return;
    }
    if (previousAgent !== agent) {
      // CopilotKit hands out a provisional agent until the runtime /info call resolves,
      // then swaps in the real one. Anything already applied to the provisional agent
      // (thread id, hydrated messages, restored picker state) must carry over, or a
      // refresh shows an empty drawer and the next send starts a new chat.
      log.info({
        agentId,
        threadId: activeThreadId
      }, 'copilot agent instance replaced; carrying thread over');
      agent.threadId = activeThreadId;
      if (agent.messages.length === 0 && previousAgent.messages.length > 0) {
        agent.setMessages(previousAgent.messages);
      }
      if (previousAgent.state != null && Object.keys(previousAgent.state as object).length > 0) {
        agent.setState(previousAgent.state);
      }
    }

    // The single place a deferred run starts: after the carry-over, so it runs on the bound agent
    // with the whole thread.
    if (isAgentRunnable()) {
      flushDeferredRun();
    }
  }, [activeThreadId, agent, agentId, cancelDeferredRun, flushDeferredRun, isAgentRunnable, runtimeConnectionStatus, stopAgentRun, switchAgentThread]);
  useEffect(() => {
    hasSelectedDatasourceRef.current = enableDatasourceSession ? datasource.hasSelectedDatasource : false;
  }, [enableDatasourceSession, datasource.hasSelectedDatasource]);

  // Datasource toggle only — not on every new message, or supplementary per-turn
  // uploaded_csv_files would be wiped after send before runAgent snapshots state.
  useEffect(() => {
    if (!enableCsvAttachment || !enableDatasourceSession) {
      return;
    }
    syncCsvRoutingState(datasource.hasSelectedDatasource, {
      messages: agent.messages
    });
  }, [enableCsvAttachment, enableDatasourceSession, datasource.hasSelectedDatasource, syncCsvRoutingState, agent]);
  useCopilotAgentChatMetaSync({
    agentState,
    currentChatId,
    onChatIdChange,
    onTitleGenerated,
    lastEmittedChatIdRef,
    titleFiredForChatRef
  });

  // Re-write datasource + knowledge session state before every run so the current
  // picker selection is on RunAgentInput.state for each turn.
  const syncSessionStateIfEnabled = useCallback(() => {
    if (enableDatasourceSession) {
      datasource.syncAgentDatasourceState();
    }
    if (enableKnowledgeSourceSession) {
      knowledgeSources.syncAgentKnowledgeSourcesState(knowledgeSources.selectedKnowledgeSources);
    }
  }, [datasource, enableDatasourceSession, enableKnowledgeSourceSession, knowledgeSources]);
  const applyPersistedSourceSelection = useCallback((selection_0: ReturnType<typeof buildPersistedSourceSelection>) => {
    if (enableDatasourceSession) {
      datasource.applyPersistedDatasource(selection_0.datasource);
    }
    if (enableKnowledgeSourceSession) {
      knowledgeSources.applyPersistedKnowledgeSources(selection_0.knowledgeSources);
    }
  }, [datasource, enableDatasourceSession, enableKnowledgeSourceSession, knowledgeSources]);
  const currentSourceSelection = useMemo(() => buildPersistedSourceSelection({
    optionIds: enableDatasourceSession ? datasource.selectedDatasourceIds : [],
    entities: enableDatasourceSession ? datasource.selectedEntities : [],
    cirrusSource: enableDatasourceSession ? datasource.selectedCirrusSource : null,
    knowledgeSources: enableKnowledgeSourceSession ? knowledgeSources.selectedKnowledgeSources : []
  }), [datasource.selectedCirrusSource, datasource.selectedDatasourceIds, datasource.selectedEntities, enableDatasourceSession, enableKnowledgeSourceSession, knowledgeSources.selectedKnowledgeSources]);
  const {
    restoreSourceSelection,
    persistSourceSelectionOnSend,
    rememberUnsentDraft
  } = useCopilotSourceSelectionPersistence({
    clientId,
    surface: sourceSelectionSurface,
    currentChatId,
    enabled: enableDatasourceSession || enableKnowledgeSourceSession,
    selection: currentSourceSelection,
    applySelection: applyPersistedSourceSelection
  });
  const applyCsvRoutingPatch = useCallback((patch: ReturnType<typeof buildAgentCsvRoutingPatch>) => {
    const currentState = agent.state != null && typeof agent.state === 'object' ? {
      ...(agent.state as Record<string, unknown>)
    } : {};
    agent.setState({
      ...currentState,
      ...patch
    });
  }, [agent]);
  const applySkillPinsPatch = useCallback((skillIds: ReadonlyArray<string | number>) => {
    const currentState_0 = agent.state != null && typeof agent.state === 'object' ? {
      ...(agent.state as Record<string, unknown>)
    } : {};
    const pinsPatch = buildAgentSkillPinsPatch(skillIds);
    agent.setState({
      ...currentState_0,
      ...pinsPatch
    });
    if (pinsPatch.selected_skill_ids.length === 0 && skillIds.length > 0) {
      log.warn({
        skillIds,
        agentId
      }, 'composer skill ids were not numeric Flow ids — pins cleared');
    }
  }, [agent, agentId]);
  const sendUserMessage = useCallback((message: string, options?: SendUserMessageOptions): boolean => {
    if (agent.isRunning || deferredRunRef.current != null) {
      log.warn({
        agentId
      }, 'dropped user message because the copilot agent is still running');
      return false;
    }
    resetCopilotFrontendToolHighlights();
    resetCopilotFrontendToolElementEffectState();
    syncSessionStateIfEnabled();
    persistSourceSelectionOnSend();
    const includeComposerAttachments = options?.includeComposerAttachments ?? true;
    // Routing reflects what is actually attached in the composer right now,
    // regardless of whether this particular send includes it: a canned
    // message (includeComposerAttachments: false) leaves the tile staged
    // for the user's own next question, and agent.state must keep describing
    // that same file, not reset as if it had been removed.
    const currentCsvFile = enableCsvAttachment ? csvAttachment.attachedFile : null;
    const attachedFile = includeComposerAttachments ? currentCsvFile : null;
    const hasSelectedDatasource = enableDatasourceSession && datasource.hasSelectedDatasource;
    const routingMode = resolveCsvRoutingMode({
      hasSelectedDatasource,
      attachedFile: currentCsvFile
    });
    const routingPatch = buildAgentCsvRoutingPatch(routingMode, currentCsvFile);
    applyCsvRoutingPatch(routingPatch);
    // Composer passes skillIds (including []) so a new turn can replace or clear
    // the pin. Canned follow-ups omit it and must keep the skill already pinned.
    if (options?.skillIds != null) {
      applySkillPinsPatch(options.skillIds);
    }

    // Images ride the message as content parts (Flow inlines their bytes for
    // the model); CSV rides agent.state and only carries a document part so
    // history can find it again.
    //
    // takeAttachments empties the composer without revoking anything —
    // gated on every tile, not just a successful upload, because a tile
    // stuck in `error` never joins the message either but must still
    // leave the composer, or it reads as still attached to whatever the
    // user types next. Skipped entirely when this send deliberately
    // excluded composer attachments — a canned message never touches
    // whatever the user has staged for their own next question.
    const takenAttachments = enableImageAttachment && includeComposerAttachments ? takeImageAttachments() : [];
    const attachedImages = takenAttachments.filter(entry_1 => entry_1.status === 'success');

    // An image that isn't joining the message (a tile stuck in `error`)
    // has nothing downstream that will ever revoke its blob, so that has
    // to happen right here. One that is joining hands its blob to the
    // thread instead — its own preview is the instant-available stand-in
    // for the not-yet-signed URL, so it must survive this function.
    const newlySentPreviews: Record<string, JustSentImagePreview> = {};
    takenAttachments.forEach(entry_2 => {
      if (entry_2.status === 'success' && entry_2.gcsUri != null && entry_2.previewUrl != null) {
        newlySentPreviews[entry_2.gcsUri] = {
          previewUrl: entry_2.previewUrl,
          filename: entry_2.filename
        };
      } else {
        revokePreviewUrl(entry_2.previewUrl);
      }
    });
    if (Object.keys(newlySentPreviews).length > 0) {
      updateJustSentImagePreviews(existing_1 => ({
        ...existing_1,
        ...newlySentPreviews
      }));
    }
    const hasAttachmentParts = attachedFile != null || attachedImages.length > 0;
    const content: AgUiUserMessage['content'] = hasAttachmentParts ? [{
      type: 'text',
      text: message
    }, ...(attachedFile == null ? [] : [{
      type: 'document',
      source: {
        type: 'url',
        value: attachedFile.gcsUri,
        mimeType: 'text/csv'
      }
    }] as const), ...attachedImages.map(image => ({
      type: 'image',
      source: {
        type: 'url',
        value: image.gcsUri as string,
        mimeType: image.mimeType
      }
    }) as const)] : message;
    const csvAttachMode = getUserMessageCsvAttachMode(routingMode);
    agent.addMessage({
      id: uuidv4(),
      role: 'user',
      content,
      ...(csvAttachMode != null ? {
        csvAttachMode
      } : {})
    });
    if (attachedFile != null) {
      csvAttachment.clearAttachment({
        clearSessionStandalone: routingMode === 'supplementary'
      });
    }
    if (currentChatId != null) {
      bumpChatUpdatedAt(currentChatId);
    }
    const runPromise = runAgentWhenReady();
    void runPromise.catch(error_0 => {
      log.warn({
        error: error_0,
        agentId
      }, 'failed to run copilot agent');
    });
    if (routingMode === 'supplementary') {
      void runPromise.finally(() => {
        clearPerTurnUploadedCsvFiles();
      });
    }
    return true;
  }, [agent, agentId, applyCsvRoutingPatch, applySkillPinsPatch, bumpChatUpdatedAt, clearPerTurnUploadedCsvFiles, csvAttachment, currentChatId, datasource.hasSelectedDatasource, enableCsvAttachment, enableDatasourceSession, enableImageAttachment, persistSourceSelectionOnSend, runAgentWhenReady, syncSessionStateIfEnabled, takeImageAttachments, updateJustSentImagePreviews]);
  const sendToolResult = useCallback(({
    toolCallId,
    toolName,
    result
  }: CopilotSendToolResultArgs) => {
    resetCopilotFrontendToolHighlights();
    resetCopilotFrontendToolElementEffectState();
    syncSessionStateIfEnabled();
    const content_0 = JSON.stringify(result);
    const existingIndex = agent.messages.findIndex(message_0 => message_0.role === 'tool' && message_0.toolCallId === toolCallId);
    if (existingIndex >= 0) {
      const nextMessages = agent.messages.map((message_1, index) => index === existingIndex ? {
        ...message_1,
        content: content_0
      } : message_1);
      agent.setMessages(nextMessages as typeof agent.messages);
    } else {
      agent.addMessage({
        id: uuidv4(),
        role: 'tool',
        toolCallId,
        content: content_0
      });
    }
    if (currentChatId != null) {
      bumpChatUpdatedAt(currentChatId);
    }
    runAgentWhenReady().catch(error_1 => {
      log.warn({
        error: error_1,
        toolCallId,
        toolName,
        agentId
      }, 'failed to resume copilot agent with tool result');
    });
  }, [agent, agentId, bumpChatUpdatedAt, currentChatId, runAgentWhenReady, syncSessionStateIfEnabled]);
  const createNewChat = useCallback((options_0?: {
    initialMessage?: string;
  }) => {
    resetCopilotFrontendToolHighlights();
    resetCopilotFrontendToolElementEffectState();
    const newThreadId = onNewChat?.();
    copilotkit.stopAgent({
      agent
    });
    cancelDeferredRun();
    resetAgentState();
    setHydrationTargetChatId(null);
    setFailedChatLoadId(null);
    if (newThreadId != null) {
      switchAgentThread(newThreadId);
    }
    if (enableDatasourceSession || enableKnowledgeSourceSession) {
      const seed = restoreSourceSelection(null);
      // initialMessage sends before the seeded picker re-renders.
      if (options_0?.initialMessage != null) {
        rememberUnsentDraft(seed);
      }
    }

    // resetAgentState() leaves {} — patch auto_add_skills: false so suggested prompts that omit skillIds stay unpinned.
    applySkillPinsPatch([]);
    if (options_0?.initialMessage != null) {
      agent.addMessage({
        id: uuidv4(),
        role: 'user',
        content: options_0.initialMessage
      });
      runAgentWhenReady().catch(error_2 => {
        log.warn({
          error: error_2,
          agentId
        }, 'failed to run copilot agent for new chat');
      });
    }
  }, [agent, agentId, applySkillPinsPatch, cancelDeferredRun, copilotkit, enableDatasourceSession, enableKnowledgeSourceSession, onNewChat, rememberUnsentDraft, resetAgentState, restoreSourceSelection, runAgentWhenReady, setHydrationTargetChatId, switchAgentThread]);
  createNewChatRef.current = () => {
    createNewChat();
  };

  // `notifyLoad: false` re-hydrates a chat that is already the active one (retry) without re-running the
  // surface's load side effects, which would confirm and persist a URL-seeded chat before the server did.
  const hydrateChat = useCallback((chatId: string, notifyLoad: boolean) => {
    copilotkit.stopAgent({
      agent
    });
    cancelDeferredRun();
    resetAgentState();
    setHydrationTargetChatId(chatId);
    switchAgentThread(chatId);
    if (enableDatasourceSession || enableKnowledgeSourceSession) {
      restoreSourceSelection(chatId);
    }

    // Same as createNewChat: clear stale pins from the prior thread and disable Flow auto-add before hydration.
    applySkillPinsPatch([]);
    // applyPersisted* already wrote agent.state — skip syncSessionStateIfEnabled() (stale ref).
    if (notifyLoad) onLoadChat?.(chatId);
  }, [agent, applySkillPinsPatch, cancelDeferredRun, copilotkit, enableDatasourceSession, enableKnowledgeSourceSession, onLoadChat, resetAgentState, restoreSourceSelection, setHydrationTargetChatId, switchAgentThread]);
  const loadChat = useCallback((chatId_0: string) => {
    hydrateChat(chatId_0, true);
  }, [hydrateChat]);
  loadChatRef.current = loadChat;
  const retryChatLoad = useCallback(() => {
    if (failedChatLoadId != null) hydrateChat(failedChatLoadId, false);
  }, [failedChatLoadId, hydrateChat]);
  const stopGeneration = useCallback(() => {
    if (deferredRunRef.current != null) {
      // Nothing is streaming yet, so dropping the deferred run is the whole stop.
      cancelDeferredRun();
      return;
    }
    stopAgentRun(agent);
  }, [agent, cancelDeferredRun, stopAgentRun]);
  return buildCopilotAgentConversationReturn({
    enableDatasourceSession,
    enableKnowledgeSourceSession,
    enableCsvAttachment,
    enableImageAttachment,
    datasource,
    knowledgeSources,
    csvAttachment,
    imageAttachments,
    justSentImagePreviewByGcsUri,
    agent,
    messages,
    sendUserMessage,
    sendToolResult,
    isLoading,
    isHydratingChat,
    chatLoadFailed,
    retryChatLoad,
    stopGeneration,
    currentChatId,
    createNewChat,
    loadChat
  });
};